// History (undo/redo) & sorting. Owns the undo/redo buttons and the Sort…
// menu (a real toolbar button + the #sortMenu popover); app.js passes the
// grid and its save scheduler explicitly. Undo/redo/sort are reachable only
// when no exclusive op is running — app.js's shared keydown guard and the
// physically-blocking overlay (withExclusiveOp) make that true by
// construction, so this module no longer needs its own locked state.
export function initHistory({ grid, scheduleSave }) {
  const undoBtn = document.getElementById('undoBtn');
  const redoBtn = document.getElementById('redoBtn');
  let history = [];      // array of name[] snapshots
  let histIndex = -1;

  const currentOrder = () => [...grid.children].map(c => c.dataset.name);
  const sameOrder = (a, b) => a.length === b.length && a.every((n, i) => n === b[i]);
  const cmpName = (a, b) => a.dataset.name.localeCompare(b.dataset.name, undefined, { numeric: true });

  function resetHistory() { history = [currentOrder()]; histIndex = 0; updateHistBtns(); }

  function pushHistory() {
    const order = currentOrder();
    if (histIndex >= 0 && sameOrder(order, history[histIndex])) return;   // nothing changed
    history = history.slice(0, histIndex + 1);
    history.push(order);
    if (history.length > 200) history.shift();
    histIndex = history.length - 1;
    updateHistBtns();
  }

  // Reorder the existing cells (a permutation of all names) to match `names`.
  function applyOrder(names) {
    const byName = new Map([...grid.children].map(c => [c.dataset.name, c]));
    const frag = document.createDocumentFragment();
    for (const n of names) { const c = byName.get(n); if (c) frag.appendChild(c); }
    grid.appendChild(frag);   // moving existing nodes reorders them in place
    scheduleSave();
  }

  function undo() { if (histIndex > 0) { applyOrder(history[--histIndex]); updateHistBtns(); } }
  function redo() { if (histIndex < history.length - 1) { applyOrder(history[++histIndex]); updateHistBtns(); } }
  function updateHistBtns() {
    undoBtn.disabled = histIndex <= 0;
    redoBtn.disabled = histIndex >= history.length - 1;
  }
  undoBtn.onclick = undo;
  redoBtn.onclick = redo;

  async function sortBy(key) {
    if (!grid.children.length) return;
    if (key === 'mtime') await ensureMtimes();
    const cells = [...grid.children].sort((a, b) =>
      key === 'name' ? cmpName(a, b)
                     : ((a._item.mtime ?? 0) - (b._item.mtime ?? 0)) || cmpName(a, b));
    applyOrder(cells.map(c => c.dataset.name));
    pushHistory();
  }
  async function ensureMtimes() {
    await Promise.all([...grid.children]
      .filter(c => c._item.mtime === undefined)
      .map(async c => {
        try { c._item.mtime = (await c._item.handle.getFile()).lastModified; }
        catch { c._item.mtime = 0; }
      }));
  }
  /* ---------- Sort menu ----------
     A real toolbar <button> + a custom .menu popover, replacing an earlier
     invisible native <select> stretched over a styled shell. Rejected because
     a native select fights the app on every front: its picker follows the
     browser's (light) color-scheme and takes no styling, it grabs and KEEPS
     keyboard focus on a mere mouse click (silencing every grid shortcut —
     app.js's keydown stands down while a form control is focused, and its
     release-on-any-key path covers buttons/links only), and dismissing the
     picker without choosing leaves no visible way out of that state. A real
     button gets app.js's blur-after-pointer-click and Tab-focus semantics for
     free; the popover is app-styled and app-dismissed. */
  const sortBtn = document.getElementById('sortBtn');
  const sortMenu = document.getElementById('sortMenu');
  const sortItems = [...sortMenu.querySelectorAll('[data-sort]')];

  function closeSortMenu(refocusBtn) {
    sortMenu.hidden = true;    // hiding also drops focus off a focused item
    if (refocusBtn) sortBtn.focus();
  }
  // click.detail is 0 for keyboard activation (Enter/Space on the focused
  // button), ≥1 for a pointer — keyboard opens with the first item focused so
  // arrow keys work immediately; a mouse open leaves focus alone (app.js blurs
  // the clicked button a microtask later, harmless once focus moved on).
  sortBtn.onclick = (e) => {
    if (!sortMenu.hidden) { closeSortMenu(false); return; }
    sortMenu.hidden = false;
    const r = sortBtn.getBoundingClientRect();
    sortMenu.style.top = (r.bottom + 4) + 'px';
    sortMenu.style.left = Math.max(4, Math.min(r.left, innerWidth - sortMenu.offsetWidth - 8)) + 'px';
    if (e.detail === 0) sortItems[0].focus();
  };
  for (const b of sortItems)
    b.onclick = (e) => { closeSortMenu(e.detail === 0); sortBy(b.dataset.sort); };

  // Outside-click close, capture phase (settings-popover style). The button
  // itself is exempt — its own onclick handles the toggle.
  addEventListener('click', (e) => {
    if (sortMenu.hidden || sortMenu.contains(e.target) || sortBtn.contains(e.target)) return;
    closeSortMenu(false);
  }, true);
  // While the menu is open it owns the keyboard (capture, before app.js's
  // shared keydown): arrows move between items whether or not one is focused
  // yet, Escape closes back to the button, Tab closes and resumes traversal
  // from the button, Enter/Space activate natively. Anything else closes the
  // menu and falls through to the grid shortcut it was aimed at.
  addEventListener('keydown', (e) => {
    if (sortMenu.hidden) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault(); e.stopPropagation();
      const idx = sortItems.indexOf(document.activeElement);
      const down = e.key === 'ArrowDown';
      sortItems[idx === -1 ? (down ? 0 : sortItems.length - 1)
                           : (idx + (down ? 1 : -1) + sortItems.length) % sortItems.length].focus();
    }
    else if (e.key === 'Escape') { e.stopPropagation(); closeSortMenu(true); }
    else if (e.key === 'Tab') closeSortMenu(true);   // native Tab then moves on from the button
    else if (!['Enter', ' ', 'Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) closeSortMenu(false);
  }, true);

  return { resetHistory, pushHistory, undo, redo };
}
